import { useSettings } from '../../hooks/queries'

/**
 * Was mit den Daten passiert — auf das Minimum gebracht.
 *
 * Das lange `docs/datenschutz-leser.md` bleibt im Repository und trägt die
 * Begründungen. Dies hier ist die Fassung für den, der sie liest: kurze
 * Schubladen statt Prosa, ähnlich den Datenschutzangaben im App Store. Es ist
 * **keine Umwandlung** des Markdowns, sondern ein eigener, gepflegter Text —
 * wer dort etwas ändert, ändert es hier mit, und umgekehrt. `CLAUDE.md` nennt
 * seit dieser Kategorie drei Stellen und nicht mehr zwei.
 *
 * **Und es sagt die Wahrheit über *diese* Installation, nicht über die Software
 * im Allgemeinen.** Die drei Ausnahmen von „keine fremden Dienste" stehen nur
 * da, wenn sie hier wirklich laufen: ohne Apple-Zugang gibt es keinen
 * Ortsnamen, ohne `GIPHY_API_KEY` keine GIF-Auswahl — und dann wäre die Zeile
 * darüber eine Behauptung. Das ist der Gewinn gegenüber einem festen Text, und
 * der Grund, warum `services` aus `/api/settings` kommt statt aus einer
 * Aufzählung hier.
 *
 * **Heute sieht die Kategorie nur die Verwaltung** (`SettingsPage`). Das ist
 * eine Stufe und keine Endfassung: sie entsteht, wird am Gerät angesehen, und
 * erst danach wird sie für die aufgemacht, für die sie geschrieben ist. Zum
 * Aufmachen sind es zwei Griffe — die Bedingung um die Kategorie fällt, und im
 * Server wandert `operator` vor das `if (!$istVerwaltung)`.
 * `tests/BetreiberTest.php` hält die heutige Stufe fest und schlägt dann fehl;
 * das ist Absicht.
 */
export function Datenschutz() {
  const settings = useSettings()
  const dienste = settings.data?.services
  const apple = dienste?.place === true || dienste?.weather === true
  /*
   * **Solange die Antwort unterwegs ist, wird hier nichts behauptet.**
   *
   * Zwei der drei Ausnahmen stehen nur da, wenn ihr Zugang eingerichtet ist —
   * und ehe der Server das gesagt hat, sähe die Seite genauso aus wie eine
   * Installation, in der sie abgeschaltet sind. Dasselbe gilt eine Zeile
   * weiter unten für den Betreiber: „noch nicht eingetragen" wäre in diesem
   * Augenblick keine Auskunft, sondern eine falsche.
   */
  const laedt = settings.data === undefined

  return (
    <>
      <div className="card card--seite">
        <h2 className="card__titel">Die eine Regel</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          <strong>Eine Antwort sieht nur der Reisende.</strong> Nicht die
          anderen Mitlesenden, auch nicht im selben Reisebericht — und das gilt
          genauso für ein Emoji und für die Zahl daneben. Das ist keine
          Einstellung, die jemand umlegen könnte, sondern die Bauart.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Was gespeichert wird</h2>

        <p className="schublade">
          <span className="schublade__name">Kontakt</span>
          <span className="schublade__text">
            E-Mail-Adresse und Anzeigename. Vom Passwort nur die Prüfsumme —
            daraus lässt es sich nicht zurückrechnen.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Inhalte</span>
          <span className="schublade__text">
            Was jemand schreibt, samt Zeitpunkt: Nachrichten, Reaktionen,
            Sprachnachrichten, GIFs — und bis wohin er gelesen hat.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Standort</span>
          <span className="schublade__text">
            Nur, was der Reisende selbst zu seiner Reise hochlädt: seine Route
            und seine Punkte. <strong>Von Mitlesenden kommt kein Standort</strong>,
            und im Hintergrund läuft nichts mit.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Kennungen</span>
          <span className="schublade__text">
            Ein Cookie fürs Angemeldetbleiben und die Kennung des Browsers
            („Safari auf einem iPhone"). Bei erlaubten Benachrichtigungen dazu
            die Adresse, an der das Gerät sie abholt.
          </span>
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Was nach außen geht</h2>
        <p className="card__lead" style={{ marginBottom: 10 }}>
          Fast nichts — und was doch, steht hier vollständig.
        </p>

        <p className="schublade">
          <span className="schublade__name">Kartenkacheln</span>
          <span className="schublade__text">
            Die Bildchen der Karte holt <strong>der Browser des Lesers</strong>{' '}
            vom Kartenserver. Dorthin gehen seine IP-Adresse, die Gegend, die er
            gerade ansieht, und der Name dieser Seite — den verlangt
            OpenStreetMap, sonst liefert es keine Karte. Nicht dorthin geht die
            Adresse der Reise, und kein Text und kein Foto.
          </span>
        </p>

        {laedt && (
          <p className="schublade">
            <span className="schublade__text">Einen Moment …</span>
          </p>
        )}

        {/* Nur, wo es wirklich läuft. Ohne Apple-Zugang heißt ein Punkt
            „Aufenthalt, 2 Std." -- und dann wäre diese Zeile eine Behauptung. */}
        {apple && (
          <p className="schublade">
            <span className="schublade__name">Ortsname und Wetter</span>
            <span className="schublade__text">
              Damit ein Punkt „Trondheim, 8 °C" heißen kann, fragt{' '}
              <strong>der Server</strong> bei Apple —{' '}
              <strong>einmal je Punkt</strong>, nie beim Ansehen. Die IP-Adresse
              eines Lesers geht dabei nirgendwohin. Ausgelöst wird es nur vom
              Reisenden.
            </span>
          </p>
        )}

        {dienste?.gif === true && (
          <p className="schublade">
            <span className="schublade__name">GIFs in Antworten</span>
            <span className="schublade__text">
              Die Suche macht <strong>der Server</strong>: hinaus geht der
              Suchbegriff, nicht die IP-Adresse. Die Vorschaubildchen holt
              dagegen der Browser unmittelbar bei Giphy, solange die Auswahl
              offen steht. Das gewählte GIF liegt danach hier — wer die Antwort
              später ansieht, schickt nichts mehr zu Giphy.
            </span>
          </p>
        )}

        <p className="schublade">
          <span className="schublade__name">Benachrichtigungen</span>
          <span className="schublade__text">
            Wer sie erlaubt, bekommt sie über den Dienst seines
            Geräteherstellers. Der erfährt, <strong>dass</strong> etwas
            unterwegs ist — nicht, <strong>was</strong> drinsteht.
          </span>
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Was nicht passiert</h2>
        <ul className="datenschutz__punkte">
          <li>Keine Werbung, kein Verkauf, keine Weitergabe an Dritte.</li>
          <li>Kein Analysedienst, kein Zählpixel, kein fremdes Cookie.</li>
          <li>Keine Auswertung der Nachrichten, durch niemanden.</li>
        </ul>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Was jeder selbst tun kann</h2>
        <ul className="datenschutz__punkte">
          <li>
            <strong>Aussteigen</strong> — in den Angaben zur Reise. Geschriebenes
            bleibt beim Reisenden; wer das nicht möchte, löscht es vorher.
          </li>
          <li>
            <strong>Benachrichtigungen abschalten</strong> — hier in den
            Einstellungen, jederzeit und ohne auszusteigen.
          </li>
          <li>
            <strong>Auskunft und Löschung</strong> — beim Betreiber, unten.
          </li>
        </ul>
      </div>

      <Verantwortlich
        betreiber={settings.data?.operator}
        darfEinrichten={settings.data?.ceiling !== undefined}
        laedt={laedt}
      />
    </>
  )
}

/**
 * Wer verantwortlich ist — und der ehrliche Satz, wenn es niemand ist.
 *
 * **Fehlt der Eintrag, steht das da**, an genau der Stelle, an der die Angabe
 * stünde. Ein leeres Feld wäre die schlechtere Auskunft: es sieht aus wie ein
 * Fehler der Anwendung, und der Leser sucht ihn.
 *
 * Der Kontakt wird **nicht verlinkt, sondern als Text gesetzt**. Was in der
 * `.env` steht, ist ein von Hand getippter Wert; daraus ein `href` zu bauen
 * hieße, fremden Text in ein Attribut zu setzen — und ein `mailto:` bringt
 * hier nichts, was eine markierbare Zeile nicht auch bringt.
 */
function Verantwortlich({
  betreiber,
  darfEinrichten,
  laedt,
}: {
  betreiber?: { name: string; address: string; contact: string }
  darfEinrichten: boolean
  /** Die Antwort ist noch unterwegs — dann steht hier weder das eine noch das andere. */
  laedt: boolean
}) {
  const vollstaendig =
    betreiber !== undefined
    && betreiber.name !== ''
    && betreiber.address !== ''
    && betreiber.contact !== ''

  return (
    <div className="card card--seite" style={{ marginBottom: 0 }}>
      <h2 className="card__titel">Verantwortlich</h2>

      {laedt ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Einen Moment …
        </p>
      ) : vollstaendig ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          {betreiber.name}
          <br />
          {betreiber.address}
          <br />
          {betreiber.contact}
        </p>
      ) : (
        <>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Der Betreiber hat seine Angaben noch nicht eingetragen. Wer
            Auskunft möchte, wendet sich an den, der ihn eingeladen hat.
          </p>

          {/* Schlüsselnamen sind für den Betrieb, nicht für den Leser. Heute
              sieht die Kategorie ohnehin nur die Verwaltung -- die Bedingung
              steht trotzdem da, damit sie beim Aufmachen nicht mitgeht. */}
          {darfEinrichten && (
            <>
              <p className="dienst__hinweis">
                Einzutragen in der <code>.env</code> auf dem Server; ein Neustart
                ist nicht nötig, die Seite zeigt es nach dem nächsten Neuladen.
              </p>
              <ul className="dienst__zeilen">
                <li>OPERATOR_NAME</li>
                <li>OPERATOR_ADDRESS</li>
                <li>OPERATOR_CONTACT</li>
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
